import { financialConverter } from '@/converters/financials/financials-converter';
import { appHttpService } from '@/services/standard/http-service';
import { models } from 'cesg-shared';

class AdminSupplierInvoiceService {
  async getAllAdminSupplierInvoices(
    supplierId?: string,
  ): Promise<models.AdminSupplierInvoicesResponse> {
    const queryParams = supplierId ? { supplierId } : {};
    const invoices = await appHttpService.request
      .url(`/admin-supplier-invoices/`)
      .query(queryParams)
      .get()
      .json<models.AdminSupplierInvoicesResponse>();
    return invoices;
  }

  async createSupplierInvoice({
    supplier,
    invoice,
    ...body
  }: models.AdminCreateSupplierInvoiceRequest): Promise<void> {
    const { totalAmount, ...invoiceWithoutTotal } = invoice;
    const strictBody = {
      ...body,
      supplier: financialConverter.toStrict(supplier),
      invoice: {
        ...financialConverter.toStrict(invoiceWithoutTotal),
        totalAmount: financialConverter.strictTotal(totalAmount),
      },
    };
    await appHttpService.request
      .url(`/admin-supplier-invoices/`)
      .post(strictBody)
      .res();
  }

  async updateSupplierInvoice(
    id: string,
    { supplier, invoice, ...body }: models.AdminUpdateSupplierInvoiceRequest,
  ): Promise<void> {
    const { totalAmount, ...invoiceWithoutTotal } = invoice;
    const strictBody = {
      ...body,
      supplier: financialConverter.toStrict(supplier),
      invoice: {
        ...financialConverter.toStrict(invoiceWithoutTotal),
        totalAmount: financialConverter.strictTotal(totalAmount),
      },
    };

    await appHttpService.request
      .url(`/admin-supplier-invoices/update/${id}`)
      .put(strictBody)
      .res();
  }

  async getAdminSupplierInvoice(
    id: string,
  ): Promise<models.GetAdminSupplierInvoiceResponse> {
    const invoice = await appHttpService.request
      .url(`/admin-supplier-invoices/${id}`)
      .get()
      .json<models.GetAdminSupplierInvoiceResponse>();
    return invoice;
  }

  async getAllSuppliers(): Promise<models.AdminGetSuppliersResponse> {
    const suppliers = await appHttpService.request
      .url(`/admin-supplier-invoices/suppliers`)
      .get()
      .json<models.AdminGetSuppliersResponse>();
    return suppliers;
  }

  async voidInvoice(id: string): Promise<void> {
    await appHttpService.request
      .url(`/admin-supplier-invoices/void/${id}`)
      .put()
      .res();
  }

  async getFilteredList(
    options: models.FilterInvoicesQuery,
  ): Promise<models.GetFilteredAdminInvoicesResponse> {
    const filteredOptions = Object.fromEntries(
      Object.entries(options).filter(([, v]) => v !== undefined),
    );
    const invoices = await appHttpService.request
      .url(`/admin-supplier-invoices/filter`)
      .query(filteredOptions)
      .get()
      .json<models.GetFilteredAdminInvoicesResponse>();
    return invoices;
  }

  async exportToCsv(
    filters: models.ExportSupplierInvoicesQuery,
  ): Promise<string> {
    const cleaned = Object.fromEntries(
      Object.entries(filters).filter(
        ([, v]) =>
          v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0),
      ),
    );
    return await appHttpService.request
      .url(`/admin-supplier-invoices/export`)
      .post(cleaned)
      .text();
  }
}

export const adminSupplierInvoiceService = new AdminSupplierInvoiceService();
